import type { Challenge, ChallengeDurationUnit, IsoDate } from '../models/types';
import { CHALLENGE_CANCEL_PENALTY } from '../models/types';
import {
  cancelChallenge,
  completeExpiredChallenge,
  createChallenge,
  getActiveChallenge,
  getProgressRatio,
  isCompletedOn,
  isWithinRange,
  toDurationDays,
  toggleChallengeDay,
} from '../services/challenge';
import { todayIso } from '../utils/date';
import { renderCheckIcon, renderIcon } from '../ui/icons';

/**
 * Challenge sayfası — CLAUDE.md §7.4.
 *
 * Aynı anda yalnızca 1 aktif challenge olabilir, bu yüzden sayfa iki
 * hâlden birinde bulunur: ya aktif challenge'ı gösterir, ya da yeni
 * challenge oluşturma formunu.
 */

/** İlerleme halkasının yarıçapı (SVG birimlerinde). */
const RING_RADIUS = 54;
/** Halkanın toplam uzunluğu — kesikli çizgi hilesinde kullanılır. */
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

interface ChallengePageData {
  today: IsoDate;
  active: Challenge | undefined;
}

function escapeHtml(text: string): string {
  const element = document.createElement('span');
  element.textContent = text;
  return element.innerHTML;
}

async function loadChallengeData(): Promise<ChallengePageData> {
  const today = todayIso();

  // Süresi dolmuş bir challenge'ı "hâlâ aktif" göstermemek için,
  // okumadan önce kapanışını yap (§7.4).
  await completeExpiredChallenge(today);

  return { today, active: await getActiveChallenge() };
}

/* ==================================================================
   Aktif challenge kartı
   ================================================================== */

/**
 * Dairesel ilerleme halkası.
 *
 * Hile şu: daireye, uzunluğu çevresine eşit kesikli bir çizgi deseni
 * veriyoruz (`stroke-dasharray`) ve sonra bu deseni ilerleme kadar
 * geri kaydırıyoruz (`stroke-dashoffset`). Sonuçta çemberin yalnızca
 * ilerleme kadarlık kısmı çizilmiş olur. `rotate(-90)` ise başlangıcı
 * sağdan tepeye taşır.
 */
function renderProgressRing(challenge: Challenge): string {
  const offset = RING_CIRCUMFERENCE * (1 - getProgressRatio(challenge));

  return `
    <div class="challenge-ring">
      <svg width="130" height="130" viewBox="0 0 130 130" aria-hidden="true">
        <circle cx="65" cy="65" r="${RING_RADIUS}" fill="none"
                stroke="oklch(100% 0 0 / 0.22)" stroke-width="12"></circle>
        <circle cx="65" cy="65" r="${RING_RADIUS}" fill="none"
                stroke="white" stroke-width="12" stroke-linecap="round"
                stroke-dasharray="${RING_CIRCUMFERENCE}"
                stroke-dashoffset="${offset}"
                transform="rotate(-90 65 65)"></circle>
      </svg>
      <div class="challenge-ring__center">
        <span class="challenge-ring__count">${challenge.completedDates.length}</span>
        <!-- Payda her zaman GÜN cinsinden: pay da işaretlenen gün
             sayısını gösteriyor, ikisi aynı birimde olmalı. Kullanıcı
             süreyi "1 ay" olarak girmiş olsa bile burada 30 gün yazar. -->
        <span class="challenge-ring__total">/ ${challenge.durationDays} gün</span>
      </div>
    </div>
  `;
}

function renderActiveChallenge(data: ChallengePageData, challenge: Challenge): string {
  const doneToday = isCompletedOn(challenge, data.today);
  const inRange = isWithinRange(challenge, data.today);

  // Kart rengi bugünün durumunu anlatır: yeşil tamamlandı,
  // kırmızı henüz yapılmadı (§5 token'ları).
  const cardModifier = doneToday ? 'challenge-card--done' : 'challenge-card--pending';

  let actionBlock: string;
  if (!inRange) {
    // Challenge yarın başlıyorsa bugünü işaretlemek anlamsız.
    actionBlock = `<p class="challenge-note">Challenge ${challenge.startDate} tarihinde başlıyor.</p>`;
  } else if (doneToday) {
    actionBlock = `
      <button type="button" class="challenge-action challenge-action--done" data-toggle-day>
        ${renderCheckIcon(16, 'white', 3)}
        Bugün Tamamlandı
      </button>
    `;
  } else {
    actionBlock = `
      <button type="button" class="challenge-action challenge-action--pending" data-toggle-day>
        Bugünkü Challenge’ı Tamamladım
      </button>
    `;
  }

  return `
    <section class="challenge-card ${cardModifier}">
      <header class="challenge-card__header">
        <h2 class="challenge-card__title">${escapeHtml(challenge.title)}</h2>
        <span class="challenge-card__badge">
          ${renderIcon('smoke', { size: 11, color: 'white' })}
          Streak’e katkı, puan yok
        </span>
      </header>
      ${renderProgressRing(challenge)}
    </section>
    ${actionBlock}
    <button type="button" class="challenge-cancel" data-ask-cancel>İptal Et</button>
    <div class="challenge-confirm" data-confirm hidden>
      <p class="challenge-confirm__title">Challenge’ı iptal etmek istediğine emin misin?</p>
      <p class="challenge-confirm__text">
        Bu işlem geri alınamaz. Başarı Barı’ndan ${CHALLENGE_CANCEL_PENALTY} puan düşülecek.
      </p>
      <div class="challenge-confirm__actions">
        <button type="button" class="challenge-confirm__no" data-abort-cancel>Vazgeç</button>
        <button type="button" class="challenge-confirm__yes" data-confirm-cancel>Evet, iptal et</button>
      </div>
    </div>
  `;
}

/* ==================================================================
   Yeni challenge formu
   ================================================================== */

function renderCreateForm(): string {
  return `
    <!-- novalidate: bkz. pages/habits.ts — iOS'ta görünmeyen yerleşik
         doğrulama balonları formu sessizce kilitliyordu. -->
    <form class="challenge-form" data-create-form novalidate>
      <h2 class="challenge-form__title">Yeni Challenge</h2>

      <label class="field">
        <span class="field__label">İsim</span>
        <input class="field__input" type="text" name="title" maxlength="60"
               placeholder="Örn. Sigarasız 30 Gün" />
      </label>

      <div class="field-row">
        <label class="field field--duration">
          <span class="field__label">Süre</span>
          <input class="field__input" type="number" name="duration"
                 min="1" max="365" value="30" inputmode="numeric" />
        </label>
        <label class="field">
          <span class="field__label">Birim</span>
          <select class="field__input" name="unit">
            <option value="day" selected>Gün</option>
            <option value="month">Ay</option>
          </select>
        </label>
      </div>

      <p class="challenge-form__hint" data-duration-hint></p>
      <p class="challenge-form__error" data-form-error hidden></p>

      <button type="submit" class="challenge-form__submit">Challenge’ı Başlat</button>
    </form>
  `;
}

/**
 * Form altındaki "bu ne kadar sürecek" özeti.
 *
 * Ay seçildiğinde kaç güne denk geldiğini göstermek önemli: §7.4'e
 * göre 1 ay her zaman 30 gündür, takvimdeki gerçek ay uzunluğu değil.
 */
function describeDuration(value: number, unit: ChallengeDurationUnit): string {
  if (!Number.isFinite(value) || value < 1) return '';
  const days = toDurationDays(value, unit);
  return unit === 'month'
    ? `${value} ay = ${days} gün (1 ay her zaman 30 gün sayılır).`
    : `${days} gün sürecek.`;
}

/* ==================================================================
   Etkileşim
   ================================================================== */

function attachActiveHandlers(
  container: HTMLElement,
  challenge: Challenge,
  today: IsoDate,
  refresh: () => Promise<void>,
): void {
  container.querySelector('[data-toggle-day]')?.addEventListener('click', () => {
    void (async () => {
      await toggleChallengeDay(challenge, today);
      await refresh();
    })();
  });

  const confirmBox = container.querySelector<HTMLElement>('[data-confirm]');
  const cancelButton = container.querySelector<HTMLElement>('[data-ask-cancel]');

  // İptal iki adımlı: önce uyarı belirir, sonra onay istenir. Geri
  // alınamayan bir işlem için tek dokunuş fazla riskli.
  cancelButton?.addEventListener('click', () => {
    if (confirmBox) confirmBox.hidden = false;
    cancelButton.hidden = true;
  });

  container.querySelector('[data-abort-cancel]')?.addEventListener('click', () => {
    if (confirmBox) confirmBox.hidden = true;
    if (cancelButton) cancelButton.hidden = false;
  });

  container.querySelector('[data-confirm-cancel]')?.addEventListener('click', () => {
    void (async () => {
      await cancelChallenge(challenge, today);
      // İptalden sonra yeni challenge oluşturma alanı hemen açılmalı
      // (§7.4) — sayfayı yeniden çizmek bunu sağlıyor.
      await refresh();
    })();
  });
}

function attachFormHandlers(
  container: HTMLElement,
  today: IsoDate,
  refresh: () => Promise<void>,
): void {
  const form = container.querySelector<HTMLFormElement>('[data-create-form]');
  if (!form) return;

  const hint = container.querySelector<HTMLElement>('[data-duration-hint]');
  const error = container.querySelector<HTMLElement>('[data-form-error]');

  const readUnit = (): ChallengeDurationUnit =>
    (form.elements.namedItem('unit') as HTMLSelectElement).value === 'month'
      ? 'month'
      : 'day';
  const readDuration = (): number =>
    Number((form.elements.namedItem('duration') as HTMLInputElement).value);

  const updateHint = (): void => {
    if (hint) hint.textContent = describeDuration(readDuration(), readUnit());
  };
  updateHint();
  form.addEventListener('input', updateHint);
  form.addEventListener('change', updateHint);

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    const title = (form.elements.namedItem('title') as HTMLInputElement).value.trim();
    const durationValue = readDuration();

    // Tarayıcının kendi doğrulaması iOS'ta her zaman görünür bir
    // uyarı vermediği için hatayı sayfada da gösteriyoruz.
    if (!title) {
      if (error) {
        error.textContent = 'Challenge’a bir isim ver.';
        error.hidden = false;
      }
      return;
    }
    // Üst sınırı da kendimiz kontrol ediyoruz: `max` özniteliği artık
    // formu engellemiyor, yalnızca sayı seçicinin sınırını çiziyor.
    if (!Number.isInteger(durationValue) || durationValue < 1 || durationValue > 365) {
      if (error) {
        error.textContent = 'Süre 1 ile 365 arasında bir tam sayı olmalı.';
        error.hidden = false;
      }
      return;
    }

    void (async () => {
      await createChallenge({ title, durationValue, unit: readUnit(), startDate: today });
      await refresh();
    })();
  });
}

/* ==================================================================
   Giriş noktası
   ================================================================== */

export async function renderChallengePage(container: HTMLElement): Promise<void> {
  const data = await loadChallengeData();
  const refresh = (): Promise<void> => renderChallengePage(container);

  container.innerHTML = `
    <h1 class="page-title">Challenge’lar</h1>
    <p class="page-subtitle">Aynı anda yalnızca 1 aktif challenge olabilir</p>
    ${data.active ? renderActiveChallenge(data, data.active) : renderCreateForm()}
  `;

  if (data.active) {
    attachActiveHandlers(container, data.active, data.today, refresh);
  } else {
    attachFormHandlers(container, data.today, refresh);
  }
}
